import { createSlice } from '@reduxjs/toolkit';

import { IFetchingStatuses } from '@/types/common';
import { getClientLog, getLogs } from 'redux/log/log.actions';

import type { LogsState, LogEntry, LogItem } from './log.types';

const initialState: LogsState = {
  data: [],
  status: null,
  total: 0,
  kind: undefined,
};

const isClientPayload = (p: any): p is { result: LogItem[]; total?: number } => p && Array.isArray(p.result);

const isLogEntryArray = (p: any): p is LogEntry[] => Array.isArray(p) && (p.length === 0 || typeof p[0]?.timestamp === 'string');

const isLogItemArray = (p: any): p is LogItem[] => Array.isArray(p) && (p.length === 0 || typeof p[0]?.entityName === 'string');

const logSlice = createSlice({
  name: 'logs',
  initialState,
  reducers: {
    clearState: (state) => {
      state.data = [];
      state.status = null;
      state.total = 0;
      state.kind = undefined;
    },
  },
  extraReducers: (builder) => {
    // GET /api/logs — новый формат (массив LogEntry)
    builder.addCase(getLogs.fulfilled, (state, { payload }) => {
      if (isLogEntryArray(payload)) {
        // исторически у тебя делался reverse — сохраним поведение
        state.data = [...payload].reverse();
        state.total = payload.length;
        state.kind = 'generic';
        state.status = IFetchingStatuses.success;
        return;
      }
      // вдруг вернулся старый формат — тоже поддержим
      if (isClientPayload(payload)) {
        state.data = payload.result ?? [];
        state.total = payload.total ?? payload.result.length;
        state.kind = 'client';
        state.status = IFetchingStatuses.success;
        return;
      }
      // fallback
      state.data = [];
      state.total = 0;
      state.kind = undefined;
      state.status = IFetchingStatuses.success;
    });
    builder.addCase(getLogs.pending, (state) => {
      state.status = IFetchingStatuses.pending;
    });
    builder.addCase(getLogs.rejected, (state) => {
      state.status = IFetchingStatuses.error;
    });

    // getClientLog — старый формат { result, total }
    builder.addCase(getClientLog.fulfilled, (state, { payload }) => {
      if (isClientPayload(payload)) {
        state.data = payload.result ?? [];
        state.total = payload.total ?? payload.result.length;
        state.kind = 'client';
      } else if (isLogItemArray(payload)) {
        // на всякий случай, если вернётся просто массив LogItem
        state.data = payload;
        state.total = payload.length;
        state.kind = 'client';
      } else if (isLogEntryArray(payload)) {
        // или внезапно новый формат
        state.data = payload;
        state.total = payload.length;
        state.kind = 'generic';
      } else {
        state.data = [];
        state.total = 0;
        state.kind = undefined;
      }
      state.status = IFetchingStatuses.success;
    });
    builder.addCase(getClientLog.pending, (state) => {
      state.status = IFetchingStatuses.pending;
    });
    builder.addCase(getClientLog.rejected, (state) => {
      state.status = IFetchingStatuses.error;
    });
  },
});

export const { clearState } = logSlice.actions;
export default logSlice.reducer;
